import { useGetMarkValidationDetail, useMarkCycleAsComplete } from "@features/mx/meterReads/hooks";
import { useToast } from "@shared/hooks/use-toast";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { useParams } from "react-router-dom";
import { toast } from "@shared/ui/use-toast";
import { useMemo } from "react";

interface MarkAsCompleteDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onGoToValidation?: () => void;
}

export function MarkAsCompleteDialog({
  open,
  setOpen,
  onGoToValidation,
}: MarkAsCompleteDialogProps) {
  const { toast } = useToast();
  const remoteUtilityId = useRemoteUtilityId();
  const { id } = useParams<{ id: string }>();
  const { data: completionData, isLoading } = useGetMarkValidationDetail(remoteUtilityId, id);
  const { mutate: markCycleAsComplete } = useMarkCycleAsComplete();

  const handleMarkComplete = () => {
    markCycleAsComplete(
      {
        payload: {
          meterRunId: id,
        },
      },
      {
        onSuccess: () => {
          toast({
          title: "Cycle marked as complete. Pending, Missing, and Exempted readings were auto-completed. New readings were added to the cycle under their routes.",
          variant: "default",
          });
          setOpen(false);
        },
        onError: () =>
          toast({
            variant: "destructive",
            title: "Failed to mark as complete",
            description: "Please try again later.",
          }),
      }
    );
  };

  const handleGoToValidation = () => {
    setOpen(false);
    onGoToValidation?.();
  };

  if (!completionData && !isLoading) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Cycle as Complete</DialogTitle>
            <DialogDescription>Data unavailable at the moment.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  const {
    dueDate,
    daysRemaining,
    newReadingsPendingValidation,
    missingReadings,
    exemptedReadings,
    ghostMeters,
  } = completionData || {};

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Mark Cycle as Complete</DialogTitle>
          <DialogDescription>
            Please review the current state before completing this cycle.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="border rounded-md p-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Due Date</p>
              <div className="mt-1 text-sm font-medium">{dueDate || "—"}</div>
            </div>
            <div className="border rounded-md p-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Days Remaining</p>
              <div className="mt-1 text-sm font-medium">
                {daysRemaining === null || daysRemaining === undefined
                  ? "—"
                  : `${daysRemaining} day${daysRemaining === 1 ? "" : "s"}`}
              </div>
            </div>
            <div className="border rounded-md p-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                New Readings Pending Validation
              </p>
              <div className="mt-1 text-sm font-medium">
                {newReadingsPendingValidation ?? "—"}
              </div>
            </div>
            <div className="border rounded-md p-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Missing Readings
              </p>
              <div className="mt-1 text-sm font-medium">{missingReadings ?? "—"}</div>
            </div>
            <div className="border rounded-md p-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Exempted Readings
              </p>
              <div className="mt-1 text-sm font-medium">{exemptedReadings ?? "—"}</div>
            </div>
            <div className="border rounded-md p-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Ghost Meters</p>
              <div className="mt-1 text-sm font-medium">{ghostMeters ?? "—"}</div>
            </div>
          </div>

          <div className="rounded-md bg-muted/40 p-3 text-xs text-muted-foreground space-y-2">
            <p className="font-medium text-foreground">What happens next</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                If you choose to proceed, all readings currently in Pending, Missing, and Exempted
                will be automatically completed by the system.
              </li>
              <li>
                All readings in the New Readings list will be added by default and associated to this
                read cycle in their detected route.
              </li>
              <li>
                You can alternatively visit the Cycle Validation page to review and validate items
                before completion.
              </li>
            </ul>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button variant="secondary" onClick={handleGoToValidation}>
            Go to Validation Page
          </Button>
          <Button onClick={handleMarkComplete}>Mark as Complete</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
